import { snakeCase, isEqual, cloneDeep } from 'lodash'
import Vue from 'vue'
import { Component, Prop, Mixins, Emit, Watch } from 'vue-property-decorator'

import { TagData } from '../Tags/types'
import { BaseInputProps } from "./fields/TextInput/types";
import { FieldSetupFunc, FieldData } from './types'

@Component({})
export class DefaultValueResetMixin extends Vue {
  isInline = false

  disabledSettingValueActive = false

  @Watch('disabledSettingValueActive')
  onChangeDefaultValueActive(value: boolean) {
    this.isInline = value
  }

  handleResetToDefault(defaultValue: any, disabledSettingValue: any) {
    this.disabledSettingValueActive = isEqual(defaultValue, disabledSettingValue)
  }

  handleDisabledValueTrigger(value:boolean) {
    if (value) {
      this.disabledSettingValueActive = true
    }
  }

  disableWrapper() {
    this.disabledSettingValueActive = false
  }
}

@Component
export class Field extends Vue {
  @Prop({ required: true }) readonly setup!: FieldSetupFunc<FieldData>

  get settings(): FieldData {
    return this.getSettings() // This variant is for preventing errors
  }

  get modelObject(): { [key: string]: any } {
    return this.settings.model
  }

  get propertyKey(): string {
    return this.settings.key
  }

  get tags(): Array<TagData> | undefined {
    return this.settings?.tags
  }

  get tariffTags(): Array<TagData> | undefined {
    return this.settings?.tariffTags
  }

  get tagsValue(): Array<TagData> {
    let tagsValue: Array<TagData> = []

    const { tariffTags } = this.settings
    if (tariffTags) {
      tagsValue = [ ...tagsValue, ...tariffTags ]
    }

    const { tags } = this.settings
    if (tags) {
      tagsValue = [ ...tagsValue, ...tags ]
    }

    return tagsValue
  }

  get defaultValue(): any {
    return this.settings.defaultValue
  }

  get settingWrapperCustomiseValue() {
    return this.settings.settingWrapper?.customiseValue
  }

  get disabledOrActiveValue() {
    if (this.settingWrapperActiveValue !== undefined) {
      return this.settingWrapperActiveValue
    }
      if (this.settingWrapperDisabledValue !== undefined) {
        return this.settingWrapperDisabledValue
      }
  }

  get settingWrapperActiveValue() {
    return this.settings.settingWrapper?.value
  }

  get settingWrapperDisabledValue() {
    return this.settings.settingWrapper?.disabledValue
  }

  get settingWrapperHasFieldButton() {
    return this.settings.settingWrapper?.hasFieldButton ?? false
  }

  get settingWrapperButtonTitle() {
    return this.settings.settingWrapper?.buttonTitle
  }

  get disabledValue(): boolean {
    if (this.setup && this.settings && this.settings.tariffTags) return true
    return this.disabled
  }

  get validation(): string {
    const { validation } = this.settings
    if (validation) {
      return validation
    }
    return ''
  }

  get disabled(): boolean {
    const { disabled } = this.settings
    if (disabled !== undefined) {
      return disabled
    }
    return false
  }

  getSettings<Type extends FieldData>(): Type {
    return this.setup.func<Type>(this.setup.args as Type)
  }

  setValue(value: any): void {
    if (this.settings.setter) {
      this.settings.setter(value)
    } else {
      this.$emit('input', value)
    }
  }

  get saveFieldKey(): boolean {
    if (this.settings.key === this.settings.saveFieldKey) {
      throw new Error('saveFieldKey prop must be different as a key')
    }
    return Boolean(this.settings?.saveFieldKey?.trim()) ?? false
  }

  get supportButtons(): boolean {
    return this.settings.supportButtons ?? true
  }
}

@Component
export class BaseInput extends Mixins(Field, DefaultValueResetMixin) {
  get value(): any {
    return cloneDeep(this.settings.model[this.settings.key])
  }

  set value(value) {
    this.setValue(value)

    if (this.saveFieldKey && this.settings.saveFieldKey) {
      this.setAccountData({ [this.settings.saveFieldKey]: value })
    }
  }

  focusOut() {
    if (this.settings.settingWrapper && isEqual(this.value, this.disabledOrActiveValue)) {
      this.disabledSettingValueActive = true
    }
  }

  onSettingClick() {
    if (this.settingWrapperCustomiseValue !== undefined) {
      this.value = this.settingWrapperCustomiseValue
    }

    this.disableWrapper();
  }

  setStubFn() {
    this.value = cloneDeep(this.disabledOrActiveValue)

    this.disabledSettingValueActive = true
  }

  setAccountData(field: Record<string, any>): void {
    this.$store.commit('AdminTemplate/SET_FIELD_TO_ACCOUNT_DATA', field)
  }

  created(): void {
    const AdminTemplate = this.$store.state.AdminTemplate;

    if (this.$store.hasModule('AdminTemplate') && AdminTemplate?.user) {
        const savedFieldValue = AdminTemplate.user.data
        const rootObjectPath = this.$store.state.AdminTemplate.accountDataRoot
        const fieldKey = this.settings.saveFieldKey

        if (this.saveFieldKey && fieldKey) {
          if (savedFieldValue && savedFieldValue[fieldKey]) {
            this.setValue(savedFieldValue[fieldKey])
          }

          if (rootObjectPath && savedFieldValue[rootObjectPath] && savedFieldValue[rootObjectPath][fieldKey]) {
            this.setValue(savedFieldValue[rootObjectPath][fieldKey])
          }
        }
    }
  }
}

@Component
export class InputFieldEmits extends Vue {
  @Emit('change')
  change(value: InputEvent): InputEvent {
    return value
  }

  @Emit('pressEnter')
  pressEnter(event: KeyboardEvent): KeyboardEvent {
    return event
  }

  @Emit('blur')
  blur(event:Event):Event {
    return event
  }
}

@Component
export class InputFieldSettings<T = any> extends Mixins<BaseInput>(BaseInput) {
  get inputSettings(): BaseInputProps<T> {
    return this.settings as BaseInputProps<T>
  }

  get size() {
    if (this.inputSettings.size) {
      return this.inputSettings.size
    }
    return 'default'
  }

  get allowClear(): boolean {
    if (this.inputSettings.allowClear !== undefined) {
      return this.inputSettings.allowClear
    }
    return false
  }

  get maxLength(): number | undefined {
    return this.inputSettings.maxLength ?? undefined
  }

  get placeholder(): string {
    return this.inputSettings.placeholder ?? ''
  }
}

@Component
export class FieldTranslationsKeys extends Mixins(Field) {
  get translationsKeysMap(): { [key: string]: any } {
    let key = snakeCase(this.propertyKey)

    const { prefix } = this.settings

    if (prefix) {
      key = prefix + key
    }

    return {
      title: `field_${ key }_title`,
      helpMessage: this.$te(`field_${ key }_help_message_project`) ? `field_${ key }_help_message_project` : `field_${ key }_help_message`,
      miniHelpMessage: this.$te(`field_${ key }_mini_help_message_project`) ? `field_${ key }_mini_help_message_project` : `field_${ key }_mini_help_message`,
      badExampleText: `field_${ key }_bad_example_text`,
      badExampleTextTitle: `field_${ key }_bad_example_text_title`,
      goodExampleText: `field_${ key }_good_example_text`,
      goodExampleTextTitle: `field_${ key }_good_example_text_title`,
    }
  }

  get propsData(): { [key: string]: any } {
    const propsData: { [key: string]: any } = {}

    // PROCESS TRANSLATIONS
    for (const key of Object.keys(this.translationsKeysMap)) {
      if ([ 'title' ].includes(key)) {
        propsData[key] = this.$t(this.translationsKeysMap[key], this.translationParams)
      } else if (key === 'helpMessage' && this.settings.customHelpMessage) {
        propsData[key] = this.settings.customHelpMessage
      } else if (key === 'miniHelpMessage' && this.settings.customMiniHelpMessage) {
        propsData[key] = this.settings.customMiniHelpMessage
      } else {
        propsData[key] = this.$te(this.translationsKeysMap[key]) ? this.$t(this.translationsKeysMap[key], this.translationParams) : ''
      }
    }

    return propsData
  }

  get translationParams(): FieldData['translationParams'] {
    if (this.settings.translationParams === undefined) return []
    return this.settings.translationParams
  }
}
